import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAddressSuggestions } from '../../services/googleMapsSite';

/**
 * Places autocomplete + Load for xlabs Locate.
 * See docs/XLABS_LOCATE.md
 */
export default function LocateAddressSearch({
  address,
  onAddressChange,
  onLoadSite,
  siteLoading,
  siteError,
  variant = 'panel', // 'panel' | 'canvas'
  autoFocus = false,
}) {
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const skipSuggestRef = useRef(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [suggestError, setSuggestError] = useState(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setSuggestOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (skipSuggestRef.current) {
      skipSuggestRef.current = false;
      return undefined;
    }
    const q = address.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setSuggestOpen(false);
      setSuggestError(null);
      return undefined;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const list = await fetchAddressSuggestions(q);
        setSuggestions(list);
        setSuggestOpen(list.length > 0);
        setActiveIndex(-1);
        setSuggestError(null);
      } catch (err) {
        setSuggestions([]);
        setSuggestOpen(false);
        setSuggestError(err?.message || String(err));
      }
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [address]);

  const pickSuggestion = useCallback(
    (item) => {
      skipSuggestRef.current = true;
      onAddressChange(item.description);
      setSuggestions([]);
      setSuggestOpen(false);
      setActiveIndex(-1);
      // Pass description so Load doesn't race React state.
      onLoadSite(item.description);
    },
    [onAddressChange, onLoadSite],
  );

  const onAddressKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestOpen && activeIndex >= 0 && suggestions[activeIndex]) {
        pickSuggestion(suggestions[activeIndex]);
        return;
      }
      setSuggestOpen(false);
      if (address.trim()) onLoadSite();
      return;
    }
    if (!suggestOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Escape') {
      setSuggestOpen(false);
      setActiveIndex(-1);
    }
  };

  const rootClass =
    variant === 'canvas' ? 'addr-search addr-search-canvas' : 'addr-search addr-search-panel';

  return (
    <div className={rootClass} ref={wrapRef}>
      <div className="addr-row">
        <div className="addr-wrap">
          <input
            type="text"
            value={address}
            onChange={(e) => onAddressChange(e.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) setSuggestOpen(true);
            }}
            placeholder="Search address…"
            autoComplete="off"
            autoFocus={autoFocus}
            aria-label="Site address"
            role="combobox"
            aria-expanded={suggestOpen}
            aria-autocomplete="list"
            aria-controls="locate-address-suggestions"
            onKeyDown={onAddressKeyDown}
          />
          {suggestOpen && suggestions.length > 0 && (
            <div
              className="suggest"
              id="locate-address-suggestions"
              role="listbox"
            >
              {suggestions.map((item, i) => (
                <button
                  key={item.placeId}
                  type="button"
                  role="option"
                  aria-selected={i === activeIndex}
                  className={i === activeIndex ? 'on' : ''}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => pickSuggestion(item)}
                >
                  {item.description}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className="button-secondary addr-load"
          onClick={onLoadSite}
          disabled={siteLoading || !address.trim()}
        >
          {siteLoading ? '…' : 'Load'}
        </button>
      </div>
      {suggestError && <div className="field-error">{suggestError}</div>}
      {siteError && <div className="field-error">{siteError}</div>}
    </div>
  );
}
